/**
 * Unified scene configuration for the DataProductGraph.
 *
 * Two-level composition chain:
 *   Sources (Invoices, Payments, Contract Terms)
 *   → Intermediates (Payables Status, Contract Status)
 *   → Final (Supplier Risk)
 */

export type CardRole = "source" | "composed";

export interface CardConfig {
  id: string;
  role: CardRole;
  /** Emphasis level — "primary" for the final output card */
  emphasis: "default" | "primary";
}

export interface EdgeConfig {
  id: string;
  from: string;
  to: string;
  /** Which anchor side on the source card */
  fromAnchor: "right" | "bottom";
  /** Which anchor side on the target card */
  toAnchor: "left" | "top";
  timing: {
    phaseOffset: number;
    period: number;
  };
}

export interface GridConfig {
  /** Dot spacing in pixels */
  spacing: number;
  /** Dot radius in pixels */
  dotRadius: number;
  /** How far from center dots begin fading (0–1, fraction of extent) */
  fadeStart: number;
  /** Color of dots */
  color: string;
}

export interface MotionConfig {
  /** Map of node ID to materialization delay (ms) */
  nodeDelays: Record<string, number>;
  /** ID of the composed node that gets the glow effect */
  composedNodeId: string;
  /** Delay before edges are drawn (after last node) */
  edgeDrawDelay: number;
  /** Delay after edge draw before edges become visible */
  edgeVisibilityDelay: number;
  /** Delay after edges visible before particles start */
  particleStartDelay: number;
  /** Delay after edges visible before composed node gets glow */
  composedGlowDelay: number;
  /** Particles per edge connector */
  particlesPerEdge: number;
}

export interface ProjectionConfig {
  /** CSS perspective value (px). 0 = flat / no perspective. */
  perspective: number;
  /** Rotation around X axis in degrees (tilt toward viewer) */
  rotateX: number;
  /** Rotation around Z axis in degrees (isometric twist) */
  rotateZ: number;
  /** Uniform scale factor applied to the scene */
  scale: number;
}

export interface SceneConfig {
  cards: CardConfig[];
  edges: EdgeConfig[];
  grid: GridConfig;
  motion: MotionConfig;
  projection: ProjectionConfig;
}

/**
 * The current scene configuration.
 * Five-node composition chain across three stages.
 */
export const sceneConfig: SceneConfig = {
  cards: [
    { id: "invoices", role: "source", emphasis: "default" },
    { id: "payments", role: "source", emphasis: "default" },
    { id: "contract-terms", role: "source", emphasis: "default" },
    { id: "payables-status", role: "composed", emphasis: "default" },
    { id: "contract-status", role: "composed", emphasis: "default" },
    { id: "supplier-risk", role: "composed", emphasis: "primary" },
  ],

  edges: [
    {
      id: "invoices-to-payables",
      from: "invoices",
      to: "payables-status",
      fromAnchor: "right",
      toAnchor: "left",
      timing: { phaseOffset: 0, period: 3400 },
    },
    {
      id: "payments-to-payables",
      from: "payments",
      to: "payables-status",
      fromAnchor: "right",
      toAnchor: "left",
      timing: { phaseOffset: 400, period: 2800 },
    },
    {
      id: "contracts-to-status",
      from: "contract-terms",
      to: "contract-status",
      fromAnchor: "right",
      toAnchor: "left",
      timing: { phaseOffset: 800, period: 2400 },
    },
    {
      id: "payables-to-risk",
      from: "payables-status",
      to: "supplier-risk",
      fromAnchor: "right",
      toAnchor: "left",
      timing: { phaseOffset: 200, period: 5200 },
    },
    {
      id: "status-to-risk",
      from: "contract-status",
      to: "supplier-risk",
      fromAnchor: "right",
      toAnchor: "left",
      timing: { phaseOffset: 600, period: 4800 },
    },
  ],

  grid: {
    spacing: 22,
    dotRadius: 1.2,
    fadeStart: 0.85,
    color: "oklch(0.78 0.008 75)",
  },

  motion: {
    nodeDelays: {
      "invoices": 300,
      "payments": 550,
      "contract-terms": 800,
      "payables-status": 1300,
      "contract-status": 1550,
      "supplier-risk": 2000,
    },
    composedNodeId: "supplier-risk",
    edgeDrawDelay: 2400,
    edgeVisibilityDelay: 80,
    particleStartDelay: 520,
    composedGlowDelay: 900,
    particlesPerEdge: 5,
  },

  projection: {
    perspective: 0,
    rotateX: 0,
    rotateZ: 0,
    scale: 1,
  },
};
